import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash2, Edit, BookOpen } from "lucide-react";

const LEVELS = ["N5", "N4", "N3", "N2", "N1"];

export default function AdminCourses() {
  const [list, setList] = useState<any[]>([]);
  const [editing, setEditing] = useState<any>(null);
  const [open, setOpen] = useState(false);

  async function load() {
    const { data } = await supabase.from("courses").select("*").order("sort_order");
    setList(data || []);
  }
  useEffect(() => { load(); }, []);

  function newItem() {
    setEditing({ slug: "", title: "", description: "", jlpt_level: "N5", published: true, sort_order: 0 });
    setOpen(true);
  }

  async function save() {
    const { id, ...rest } = editing;
    const op = id ? supabase.from("courses").update(rest).eq("id", id) : supabase.from("courses").insert(rest);
    const { error } = await op;
    if (error) toast.error(error.message);
    else { toast.success("Saved"); setOpen(false); load(); }
  }

  async function del(id: string) {
    if (!confirm("Delete course and all lessons?")) return;
    await supabase.from("courses").delete().eq("id", id);
    load();
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Courses</h2>
        <Button onClick={newItem}>Add course</Button>
      </div>
      <div className="grid gap-3">
        {list.map((c) => (
          <Card key={c.id}>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <div className="font-semibold">{c.title} <span className="text-xs text-muted-foreground">[{c.jlpt_level}]</span></div>
                <div className="text-sm text-muted-foreground">{c.slug} {!c.published && "(draft)"}</div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" asChild><Link to={`/admin/courses/${c.id}/lessons`}><BookOpen className="h-4 w-4 mr-1" />Lessons</Link></Button>
                <Button size="icon" variant="ghost" onClick={() => { setEditing(c); setOpen(true); }}><Edit className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => del(c.id)}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing?.id ? "Edit" : "New"} course</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label>Slug</Label><Input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} /></div>
              <div><Label>Title</Label><Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></div>
              <div><Label>Description</Label><Textarea value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>JLPT level</Label>
                  <select className="w-full h-10 rounded-md border px-2 bg-background" value={editing.jlpt_level} onChange={(e) => setEditing({ ...editing, jlpt_level: e.target.value })}>
                    {LEVELS.map((l) => <option key={l}>{l}</option>)}
                  </select>
                </div>
                <div><Label>Sort order</Label><Input type="number" value={editing.sort_order} onChange={(e) => setEditing({ ...editing, sort_order: +e.target.value })} /></div>
              </div>
              <label className="flex items-center gap-2"><input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} /> Published</label>
              <Button onClick={save} className="w-full">Save</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
